import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  TablePagination,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import SearchIcon from "@mui/icons-material/Search";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import authService from "../../../utils/authService";
import { fetchBatchesByCourse } from "../../../utils/batchUtils";

const STATUS_OPTIONS = ["request_approved", "review_request", "closed"] as const;

type ProfileRequestStatus = (typeof STATUS_OPTIONS)[number];

interface Course {
  course_id: string;
  course_name: string;
  id?: number;
  course_db_id?: number;
}

interface StudentProfileRequestItem {
  request_id: number;
  student_id: string;
  student_firstname: string;
  student_lastname: string;
  student_email: string;
  batch_id: string;
  batch_name: string;
  course_db_id: number;
  course_code: string;
  status: string;
  description: string;
  date_time: string;
  json_data: Record<string, unknown>;
  total_profile_request_rows: number;
  status_counts: Record<string, number>;
  student_ack_active?: boolean;
}

interface StudentProfileRequestListResponse {
  total: number;
  skip: number;
  limit: number;
  items: StudentProfileRequestItem[];
}

function jsonDataFieldKeys(json: Record<string, unknown> | undefined): string {
  if (!json || typeof json !== "object") return "—";
  const keys = Object.keys(json);
  return keys.length ? keys.join(", ") : "—";
}

const headCellSx = {
  backgroundColor: "#f5f5f5",
  borderRight: "1px solid #e0e0e0",
  fontWeight: 600,
  fontSize: "12px",
  padding: "8px 12px",
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
};

const bodyCellSx = {
  padding: "6px 12px",
  fontSize: "12px",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
};

const StudentProfileApproval: React.FC = () => {
  const FILTERS_STORAGE_KEY = "admin_student_profile_approval_filters";
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<{ batch_id: string; batch_name: string }[]>([]);
  const [selectedCourseCode, setSelectedCourseCode] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [status, setStatus] = useState<ProfileRequestStatus>("review_request");
  const [items, setItems] = useState<StudentProfileRequestItem[]>([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [showSearchBox, setShowSearchBox] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<{ requestId: number; kind: "unlock" | "decline" } | null>(null);
  const [restoredRequestState, setRestoredRequestState] = useState<{
    skip: number;
    limit: number;
    selectedCourseCode: string;
    selectedBatch: string;
  } | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const resolveCourseDbId = (c: Course): number | undefined => {
    if (typeof c.id === "number" && !Number.isNaN(c.id)) return c.id;
    if (typeof c.course_db_id === "number" && !Number.isNaN(c.course_db_id)) return c.course_db_id;
    const rawId = (c as { id?: string | number }).id;
    if (typeof rawId === "string" && /^\d+$/.test(rawId)) return parseInt(rawId, 10);
    if (typeof c.course_id === "string" && /^\d+$/.test(c.course_id)) return parseInt(c.course_id, 10);
    return undefined;
  };

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as {
          status?: ProfileRequestStatus;
          selectedCourseCode?: string;
          selectedBatch?: string;
          skip?: number;
          limit?: number;
        };
        if (parsed.status && STATUS_OPTIONS.includes(parsed.status)) {
          setStatus(parsed.status);
        }
        if (typeof parsed.limit === "number" && parsed.limit > 0) {
          setLimit(parsed.limit);
        }
        if (parsed.selectedCourseCode) {
          setSelectedCourseCode(parsed.selectedCourseCode);
        }
        setRestoredRequestState({
          skip: typeof parsed.skip === "number" && parsed.skip >= 0 ? parsed.skip : 0,
          limit: typeof parsed.limit === "number" && parsed.limit > 0 ? parsed.limit : 25,
          selectedCourseCode: parsed.selectedCourseCode || "",
          selectedBatch: parsed.selectedBatch || "",
        });
      }
    } catch (e) {
      console.error("Failed to restore student profile approval filters:", e);
    }

    const loadCourses = async () => {
      setLoadingCourses(true);
      try {
        const response = await authService.get("admin/batch/get_all_courses_for_batch/");
        setCourses(response.data.courses || []);
      } catch (e) {
        console.error("Error fetching courses:", e);
        setError("Could not load courses.");
      } finally {
        setLoadingCourses(false);
      }
    };
    loadCourses();
  }, []);

  useEffect(() => {
    if (!selectedCourseCode) {
      setBatches([]);
      setSelectedBatch("");
      return;
    }
    const loadBatches = async () => {
      setLoadingBatches(true);
      try {
        const list = await fetchBatchesByCourse(selectedCourseCode);
        setBatches(
          list
            .filter((b) => !b.del_row)
            .map((b) => ({ batch_id: b.batch_id, batch_name: b.batch_name }))
        );
      } catch (e) {
        console.error("Error fetching batches:", e);
        setBatches([]);
      } finally {
        setLoadingBatches(false);
      }
    };
    loadBatches();
  }, [selectedCourseCode]);

  useEffect(() => {
    if (!restoredRequestState) return;
    if (!restoredRequestState.selectedBatch) return;
    if (!selectedCourseCode || selectedCourseCode !== restoredRequestState.selectedCourseCode) return;
    if (selectedBatch) return;
    const exists = batches.some((b) => b.batch_id === restoredRequestState.selectedBatch);
    if (exists) setSelectedBatch(restoredRequestState.selectedBatch);
  }, [batches, restoredRequestState, selectedCourseCode, selectedBatch]);

  useEffect(() => {
    if (!restoredRequestState) return;
    const waitingForCourse =
      restoredRequestState.selectedCourseCode && selectedCourseCode !== restoredRequestState.selectedCourseCode;
    const waitingForBatch =
      restoredRequestState.selectedBatch &&
      selectedCourseCode === restoredRequestState.selectedCourseCode &&
      selectedBatch !== restoredRequestState.selectedBatch;
    if (waitingForCourse || waitingForBatch || loadingBatches) return;

    if (restoredRequestState.limit !== limit) {
      setLimit(restoredRequestState.limit);
    }
    fetchPage(restoredRequestState.skip, restoredRequestState.limit);
    setRestoredRequestState(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredRequestState, selectedCourseCode, selectedBatch, loadingBatches]);

  useEffect(() => {
    sessionStorage.setItem(
      FILTERS_STORAGE_KEY,
      JSON.stringify({
        status,
        selectedCourseCode,
        selectedBatch,
        skip,
        limit,
      })
    );
  }, [status, selectedCourseCode, selectedBatch, skip, limit]);

  useEffect(() => {
    if (showSearchBox) {
      searchInputRef.current?.focus();
    }
  }, [showSearchBox]);

  const fetchPage = async (nextSkip: number, limitOverride?: number) => {
    const pageLimit = limitOverride ?? limit;
    setLoadingList(true);
    setItems([]);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        status,
        skip: nextSkip,
        limit: pageLimit,
      };

      if (selectedCourseCode) {
        const course = courses.find((c) => c.course_id === selectedCourseCode);
        const dbId = course ? resolveCourseDbId(course) : undefined;
        params.course_id = dbId !== undefined ? dbId : selectedCourseCode;
      }

      if (selectedBatch) params.batch_id = selectedBatch;

      const response = await authService.get("admin/student-profile-request/", { params });
      const data = response.data as StudentProfileRequestListResponse;
      setItems(data.items || []);
      setTotal(typeof data.total === "number" ? data.total : 0);
      setSkip(typeof data.skip === "number" ? data.skip : nextSkip);
    } catch (e) {
      console.error("Error loading profile requests:", e);
      setError("Failed to load requests.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoadingList(false);
    }
  };

  const applyFilters = () => fetchPage(0);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const removeRow = (requestId: number) => {
    setItems((prev) => prev.filter((r) => r.request_id !== requestId));
    setTotal((t) => Math.max(0, t - 1));
  };

  const handleUnlock = async (requestId: number) => {
    setActing({ requestId, kind: "unlock" });
    setError(null);
    try {
      await authService.put(`admin/student-profile-request/${requestId}/unlock/`, {});
      removeRow(requestId);
    } catch (e) {
      console.error("Error unlocking profile request:", e);
      setError("Approve (allow edit) failed.");
    } finally {
      setActing(null);
    }
  };

  const handleDecline = async (requestId: number) => {
    setActing({ requestId, kind: "decline" });
    setError(null);
    try {
      await authService.put(`admin/student-profile-request/${requestId}/decline/`, {});
      removeRow(requestId);
    } catch (e) {
      console.error("Error declining profile request:", e);
      setError("Decline (reject ask) failed.");
    } finally {
      setActing(null);
    }
  };

  const handleStatusChange = (e: SelectChangeEvent<string>) => {
    setStatus(e.target.value as ProfileRequestStatus);
  };

  const handleCourseChange = (e: SelectChangeEvent<string>) => {
    setSelectedCourseCode(e.target.value);
    setSelectedBatch("");
  };

  const handleBatchChange = (e: SelectChangeEvent<string>) => {
    setSelectedBatch(e.target.value);
  };

  const colCount = status === "review_request" ? 6 : 5;
  const pageIndex = limit > 0 ? Math.floor(skip / limit) : 0;
  const normalizedSearch = searchText.trim().toLowerCase();
  const visibleItems =
    normalizedSearch.length === 0
      ? items
      : items.filter((row) => {
          const name = `${row.student_firstname || ""} ${row.student_lastname || ""}`.trim().toLowerCase();
          const email = (row.student_email || "").toLowerCase();
          const description = (row.description || "").replace(/<[^>]+>/g, "").toLowerCase();
          const fields = jsonDataFieldKeys(row.json_data).toLowerCase();
          return (
            name.includes(normalizedSearch) ||
            email.includes(normalizedSearch) ||
            description.includes(normalizedSearch) ||
            fields.includes(normalizedSearch)
          );
        });

  return (
    <div className="container-fluid p-0 pe-2" style={{ marginTop: "12px" }}>
      <Card>
        <CardContent
          style={{
            height: "calc(100vh - 90px)",
            overflow: "hidden",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1} style={{ flexShrink: 0 }}>
            <Box display="flex" gap="12px" alignItems="center" flexWrap="wrap">
              <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>Status</InputLabel>
                <Select
                  value={status}
                  onChange={handleStatusChange}
                  label="Status"
                  disabled={loadingList}
                  style={{ height: "36px", fontSize: "14px", fontFamily: "inherit" }}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s} style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>Course</InputLabel>
                <Select
                  value={selectedCourseCode}
                  onChange={handleCourseChange}
                  label="Course"
                  disabled={loadingCourses || loadingList}
                  style={{ height: "36px", fontSize: "14px", fontFamily: "inherit" }}
                >
                  <MenuItem value="" style={{ fontSize: "14px", fontFamily: "inherit" }}>
                    All courses
                  </MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.course_id} value={c.course_id} style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      {c.course_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                <InputLabel style={{ fontSize: "14px", fontFamily: "inherit" }}>Batch</InputLabel>
                <Select
                  value={selectedBatch}
                  onChange={handleBatchChange}
                  label="Batch"
                  disabled={!selectedCourseCode || loadingBatches || loadingList}
                  style={{ height: "36px", fontSize: "14px", fontFamily: "inherit" }}
                >
                  <MenuItem value="" style={{ fontSize: "14px", fontFamily: "inherit" }}>
                    {selectedCourseCode ? "All batches" : "Select course first"}
                  </MenuItem>
                  {batches.map((b) => (
                    <MenuItem key={b.batch_id} value={b.batch_id} style={{ fontSize: "14px", fontFamily: "inherit" }}>
                      {b.batch_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {showSearchBox && (
                <TextField
                  inputRef={searchInputRef}
                  size="small"
                  placeholder="Search name / description / fields"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ minWidth: 260 }}
                  InputProps={{
                    style: { height: "36px", fontSize: "13px", fontFamily: "inherit" },
                  }}
                />
              )}
                <SearchIcon fontSize="small" color="primary"  onClick={() => setShowSearchBox((prev) => !prev)}
                />
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={applyFilters}
                disabled={loadingList}
                style={{ height: "36px", fontSize: "13px", textTransform: "none" }}
              >
                {loadingList ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ marginRight: 8 }} />
                    Loading…
                  </>
                ) : (
                  "Apply"
                )}
              </Button>
            </Box>
          </Box>

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {error}
            </Typography>
          )}

          <TableContainer component={Paper} style={{ marginTop: "8px", flexGrow: 1, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, minWidth: 56 }}>Sl no</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Student name</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 140 }}>Date</TableCell>
                  <TableCell sx={{ ...headCellSx, minWidth: 200 }}>Description</TableCell>
                  {status === "review_request" && (
                    <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Fields</TableCell>
                  )}
                  <TableCell sx={{ ...headCellSx, minWidth: 120, textAlign: "center" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingList ? (
                  <TableRow>
                    <TableCell colSpan={colCount} align="center" style={{ padding: "24px" }}>
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </TableCell>
                  </TableRow>
                ) : visibleItems.length > 0 ? (
                  visibleItems.map((row, index) => (
                    <TableRow
                      key={row.request_id}
                      sx={{
                        backgroundColor: row.student_ack_active
                          ? "#fff3cd"
                          : index % 2 === 0
                            ? "#ffffff"
                            : "#fafafa",
                        "&:hover": {
                          backgroundColor: row.student_ack_active ? "#ffe69c" : "#f0f0f0",
                        },
                      }}
                    >
                      <TableCell sx={bodyCellSx}>{skip + index + 1}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>
                        <div>{[row.student_firstname, row.student_lastname].filter(Boolean).join(" ") || "—"}</div>
                        <div style={{ fontSize: "11px", color: "#666" }}>{row.student_email || "—"}</div>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{formatDate(row.date_time)}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word" }}>
                        {row.description?.replace(/<[^>]+>/g, "") || "—"}
                      </TableCell>
                      {status === "review_request" && (
                        <TableCell sx={{ ...bodyCellSx, maxWidth: 280, whiteSpace: "normal" }}>
                          {jsonDataFieldKeys(row.json_data)}
                        </TableCell>
                      )}
                      <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                        {row.status === "review_request" ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() =>
                              navigate(`/admin/student-profile-approval/${row.request_id}`, { state: { item: row } })
                            }
                            style={{ fontSize: "11px", padding: "4px 12px", minWidth: "60px", textTransform: "none" }}
                          >
                            View
                          </Button>
                        ) : row.status === "request_raised" ? (
                          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={0.5} alignItems="center" justifyContent="center">
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              disabled={acting !== null}
                              onClick={() => handleUnlock(row.request_id)}
                              style={{ fontSize: "11px", textTransform: "none" }}
                            >
                              {acting?.requestId === row.request_id && acting.kind === "unlock" ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Approve"
                              )}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              disabled={acting !== null}
                              onClick={() => handleDecline(row.request_id)}
                              style={{ fontSize: "11px", textTransform: "none" }}
                            >
                              {acting?.requestId === row.request_id && acting.kind === "decline" ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Decline"
                              )}
                            </Button>
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={colCount}
                      align="center"
                      style={{ padding: "24px", color: "#666", fontSize: "14px", fontStyle: "italic" }}
                    >
                      {searchText.trim()
                        ? "No matching records found for the search."
                        : "No profile requests for the selected filters. Click Apply to load."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={total === 0 ? 0 : pageIndex}
            onPageChange={(_, newPage) => {
              setLoadingList(true);
              fetchPage(newPage * limit);
            }}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setLoadingList(true);
              setLimit(next);
              void fetchPage(0, next);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            disabled={loadingList}
            sx={{
              flexShrink: 0,
              borderTop: "1px solid #e5e7eb",
              backgroundColor: "#fafafa",
              "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
                color: "#666",
                mb: 0,
              },
              "& .MuiTablePagination-actions": { ml: 1 },
              "& .MuiIconButton-root": { p: "6px" },
              "& .Mui-disabled": { opacity: 0.45 },
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentProfileApproval;
